<script setup lang="ts">
/**
 * <peppermill-app-header> handles header UX for <peppermill-app-container>.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.2.0
 * @changelog
 *  2023-01-16 (1.0.0) (jhoward)
 *    - <peppermill-app-header> displays the current page title.
 *    - <peppermill-app-header> collapses on scroll.
 *
 * 2026-03-11 (1.1.0) (jhoward)
 *   - Add AppHeader.vue for displaying page title and scroll/collapse behavior.
 *
 * 2026-03-30 (1.1.1) (jhoward)
 *   - Change root element from <div> to <header> in template.
 *
 * 2026-04-09 (1.1.2) (jhoward)
 *   - Render title using v-html in src\components\AppHeader.vue.
 *
 * 2026-10-09 (1.2.0) (jhoward)
 *   - Read the bar and title colours, the title type (display medium) and the collapse elevation (level 2) from the
 *     framework's Material Design 3 tokens, in place of the raw brand triplet, #fff, robotolight and a literal shadow.
 *   - Remove the floating-action-button rules, which were scoped to this component and had nothing to match;
 *     CountdownCallToAction.vue and the framework's <peppermill-call-to-action> draw the buttons.
 *   - Take the default expanded and collapsed heights from helpers/headerMetrics, which the router also reads.
 *   - Start collapsing only once the sticky header has reached the top of the viewport (getHeaderNaturalTop). It used to
 *     translate by the whole scroll distance while the page was also carrying it up, so it left at twice the speed.
 */

import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import PeppermillViewport from '@/components/framework-proxy/PeppermillViewport.vue';
import { getHeaderNaturalTop, HEADER_MAX_HEIGHT, HEADER_MIN_HEIGHT } from '@/helpers/headerMetrics';
import useAppStore from '@/store';

const props = defineProps({
  maxHeight: {
    type: Number,
    default: HEADER_MAX_HEIGHT,
  },
  minHeight: {
    type: Number,
    default: HEADER_MIN_HEIGHT,
  },
  titleMaxLeft: {
    type: Number,
    default: 90,
  },
  titleMinLeft: {
    type: Number,
    default: 0,
  },
  titleMaxSize: {
    type: Number,
    default: 1,
  },
  titleMinSize: {
    type: Number,
    default: 0.7,
  },
  shadowMaxOpacity: {
    type: Number,
    default: 0.34,
  },
  shadowMinOpacity: {
    type: Number,
    default: 0,
  },
});

function render() {
  if (windowScrollPosition.value !== lastRenderScrollPosition.value) {
    lastRenderScrollPosition.value = windowScrollPosition.value;

    //The header is sticky, so the page carries it up on its own until it reaches the top; collapsing it as well from
    //the first pixel of scroll sent it up at twice the page's speed. It collapses only by how far it has stuck.
    const collapsed = Math.max(0, windowScrollPosition.value - getHeaderNaturalTop());
    const newHeight =
      collapsed > props.maxHeight - props.minHeight ? props.minHeight : props.maxHeight - collapsed;
    const percent = (props.maxHeight - newHeight) / (props.maxHeight - props.minHeight);
    const newH1Left = props.titleMinLeft + (props.titleMaxLeft - props.titleMinLeft) * (1 - percent);

    containerTransform.value = 'translateY(' + -1 * (props.maxHeight - newHeight) + 'px)';
    shadowOpacity.value = percent;
    titleTransform.value =
      'translateX(' +
      newH1Left +
      'px) scale(' +
      Math.max(props.titleMinSize, Math.min(props.titleMaxSize, 1 - percent)) +
      ')';
  }

  requestAnimationFrame(render);
}

function onScroll() {
  if (htmlNode.value) {
    windowScrollPosition.value = htmlNode.value.scrollTop;
  }
}

function initialize() {
  if (htmlNode.value) {
    windowScrollPosition.value = htmlNode.value.scrollTop;
  }
  lastRenderScrollPosition.value = -1;
  requestAnimationFrame(render);
}

const appStore = useAppStore();
const windowScrollPosition = ref<number>(0);
const lastRenderScrollPosition = ref<number>(-1);
const containerTransform = ref<string>('none');
const titleTransform = ref<string>('none');
const shadowOpacity = ref<number>(1);
const title = ref<string>('title');
let titleTimer = -1;
const htmlNode = ref<HTMLElement | null>(null);
const { pageTitle } = storeToRefs(appStore);

const containerStyles = computed(() => {
  return {
    transform: containerTransform.value,
  };
});
const titleStyles = computed(() => {
  return {
    transform: titleTransform.value,
  };
});
const shadowStyles = computed(() => {
  return {
    opacity: shadowOpacity.value,
  };
});

watch(pageTitle, () => {
  const newTitle = appStore.pageTitle.length ? appStore.pageTitle : appStore.appTitle;
  if (newTitle !== title.value) {
    clearTimeout(titleTimer);
    title.value = '';
    titleTimer = window.setTimeout(() => {
      title.value = newTitle;
    }, 150);
  }
});

onMounted(() => {
  htmlNode.value = document.querySelector('html');

  window.addEventListener('load', initialize, { passive: true });
  if ('complete' === document.readyState || 'interactive' === document.readyState) {
    initialize();
  } else {
    document.addEventListener('DOMContentLoaded', initialize);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', initialize, { passive: true });

  title.value = appStore.pageTitle;

  initialize();
});
</script>

<template>
  <header class="header--container" :style="containerStyles">
    <peppermill-viewport>
      <Transition appear>
        <h1 class="header--title" :style="titleStyles" v-if="title.length" v-html="title"></h1>
      </Transition>
    </peppermill-viewport>
    <div class="header--shadow" :style="shadowStyles"></div>
  </header>
</template>

<style scoped>
.header--container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 200px;
  padding: 0 16px;
  z-index: 20;
  background-color: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font-family: var(--md-sys-typescale-display-medium-font);
  text-rendering: optimizeLegibility;
  will-change: transform;
}

.layout-no_nav .header--container {
  padding-left: 16px;
}

.header--container .header--shadow {
  content: '';
  position: absolute;
  z-index: -1;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  box-shadow: var(--md-sys-elevation-level2);
  opacity: 0;
  will-change: opacity;
}

.header--container .header--title {
  position: absolute;
  bottom: 12px;
  margin: 0;
  color: var(--md-sys-color-on-primary);
  font-size: var(--md-sys-typescale-display-medium-size);
  font-weight: var(--md-sys-typescale-display-medium-weight);
  line-height: var(--md-sys-typescale-display-medium-line-height, 1.2);
  /*padding-left: 120px;*/
  will-change: transform;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  -webkit-transform-origin: 1% 100%;
  transform-origin: 1% 100%;
}

@media (max-width: 800px) {
  .header--container .header--title {
    font-size: var(--md-sys-typescale-display-medium-size);
    bottom: 18px;
    transform: translateX(0px) scale(0.5) !important;
  }
}

@media (max-width: 1400px) {
  .header--container {
    padding: 0 72px;
  }
  .app--container-hide_nav .header--container {
    padding: 0 72px 0 1rem;
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.v-leave-from,
.v-enter-to {
  opacity: 1;
}
</style>
